import Accordion from "@/common/components/Accordion"
import Checkbox from "@/common/components/Checkbox"
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder"
import MaskedTextField from "@/common/components/MaskedTextField"
import TextField from "@/common/components/TextField"
import { cnpjCpfMask } from "@/common/constants/masks.constant"
import { strategy as kindStrategy } from "@/features/companies/constants/kind.constant"
import { useCompany } from "@/features/privateCompanies/hooks/useCompany"
import { CompanyBranchData } from "@/features/privateCompanies/types/companyBranches.type"
import { BriefcaseBusinessIcon } from "lucide-react"
import { useCompanyBranch } from "../../hooks/useCompanyBranch"
import AddressForm from "@/common/components/AddressForm";
import useAddressForm from "@/common/hooks/useAddressForm";
import { AddressData } from "@/common/types/address";

export type GeneralInformationProps = {
  companyId?: string
  generalInformation?: CompanyBranchData['general_information']
  parentId?: string
  branchDataIsFetching: boolean;
}

export const GeneralInformation = ({
  companyId,
  generalInformation,
  parentId,
  branchDataIsFetching,
}: GeneralInformationProps) => {
  const { data: companyData } = useCompany(companyId || '')

  const { data: branchData } = useCompanyBranch(parentId || '', !!parentId);
  const branchFormData =  generalInformation || branchData?.general_information || companyData?.general_information || null

  const addressForm = useAddressForm({
    initialAddress: branchFormData?.address as AddressData || {},
    disabled: true,
    isLoading: branchDataIsFetching,
  });

  return (
    <Accordion
      name="general_information"
      head={(
        <div className="flex flex-col gap-2 ">
          <Checkbox
            label='Dados empresariais específicos'
            name='general_information_willUseOwnData'
            checked={!!generalInformation}
            disabled
          />
        </div>
      )}
      body={(
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            <div className="flex gap-2 w-full">
              <div>
                <p>Logo</p>
                <ImageWithPlaceholder
                  className="size-20"
                  iconClassName="size-14"
                  placeholderIcon={BriefcaseBusinessIcon}
                  url={generalInformation?.company_logo?.url}
                  alt={generalInformation?.fantasy_name}
                />
              </div>
              <MaskedTextField
                label="CNPJ/CPF"
                name='general_information_cnpj'
                mask={cnpjCpfMask}
                className="w-full mt-[14px]"
                value={branchFormData?.cnpj || ''}
                disabled
              />
            </div>
            <TextField
              label="Tipo de empresa"
              className="w-full mt-[14px]"
              placeholder="Privada"
              value={companyData?.kind ? kindStrategy[companyData.kind as keyof typeof kindStrategy] : ''}
              disabled
            />
          </div>
          <div className="flex gap-2">
            <TextField
              name="general_information.fantasy_name"
              label="Nome Fantasia"
              className="w-1/2"
              value={branchFormData?.fantasy_name || ''}
              disabled
            />
            <TextField
              name="general_information.official_name"
              label="Nome Empresarial"
              className="w-1/2"
              value={branchFormData?.official_name || ''}
              disabled
            />
          </div>
          <TextField
            name="general_information.email"
            label="Endereço eletrônico"
            maxLength={64}
            value={branchFormData?.email || ''}
            disabled
          />
          <div className="flex gap-2">
            <TextField
              name="general_information.state_inscription"
              label="IE (Inscrição Estadual)"
              maxLength={14}
              className="w-1/2"
              value={branchFormData?.state_inscription || ''}
              disabled
            />
            <TextField
              name="general_information.primary_cnae"
              label="CNAE"
              maxLength={7}
              className="w-1/2"
              value={branchFormData?.primary_cnae || ''}
              disabled
            />
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex justify-between">
              <p className="text-[18px] font-bold">Endereço</p>
            </div>
            <AddressForm
              addressForm={addressForm}
              disableFields
            />
          </div>
        </div>
      )}
    />
  )
}
